import { FunctionComponent, PropsWithChildren } from 'react';

import { useField } from 'react-final-form';

import {
  Divider,
  Icons,
  LabeledRadio,
  Tooltip,
  ExternalLink,
  Typography,
  Stack,
} from '@cast/design-system';

import { OptimizationThresholdType } from '../../../_types';
import { ResourceInput } from '../Resource/ResourceInput';
import { ResourceRadioGroup } from '../Resource/ResourceRadioGroup';

import { useControlsGroupState } from '@cast/shared-rff-ui';

import { getDocsLink } from '@cast/utils';

const label = { cpu: 'CPU', memory: 'Memory' };

type ResourceThresholdFieldProps = {
  resource: keyof typeof label;
  LabelComponent?: FunctionComponent<PropsWithChildren>;
  size: 'medium' | 'large';
};

const ResourceThresholdInput = ({
  resource,
  LabelComponent = ({ children }) => <>{children}</>,
  size,
}: ResourceThresholdFieldProps) => {
  // if not wrapped on `RffControlsProvider` typeFieldName='resource.type'
  const { name: typeFieldName = '' } = useControlsGroupState({
    name: `${resource}.type`,
  });

  const isDynamicThreshold =
    useField<OptimizationThresholdType>(typeFieldName).input.value ===
    OptimizationThresholdType.DYNAMIC;

  const textVariant = size === 'medium' ? 'P10R' : 'P12R';
  const labelVariant = size === 'medium' ? 'P10M' : 'P12M';

  return (
    <ResourceRadioGroup resource={resource} name="type">
      <Stack gap={4}>
        <LabelComponent>
          <Typography variant={labelVariant}>{label[resource]}</Typography>
        </LabelComponent>

        <Stack direction="row" alignItems="center" gap={12}>
          <LabeledRadio
            size="xs"
            label="Percentage"
            labelProps={{ variant: textVariant }}
            radioProps={{
              value: OptimizationThresholdType.PERCENTAGE,
              testId: `${resource}-percentile-option`,
            }}
          />

          <ResourceInput
            resource={resource}
            name="percentage"
            variant="percentage"
            disabled={isDynamicThreshold}
          />
        </Stack>

        <Stack direction="row" alignItems="center" gap={4}>
          <LabeledRadio
            size="xs"
            label="Dynamic"
            labelProps={{ variant: textVariant }}
            radioProps={{
              value: OptimizationThresholdType.DYNAMIC,
              testId: `${resource}-dynamic-option`,
            }}
          />
          <Tooltip
            title={
              <Typography variant="P12R">
                Automatically adjusts the threshold based on workload size,
                providing a balance between stability and optimization across
                different workloads, ensuring stable scaling behavior without
                excessive restarts for small workloads or delays for large ones.{' '}
                <ExternalLink
                  link={getDocsLink('docs_woop_sensitivity')}
                  noIcon
                >
                  Learn more
                </ExternalLink>
              </Typography>
            }
            placement="top"
            arrow
          >
            <Icons.Info fontSize="small" />
          </Tooltip>
        </Stack>
      </Stack>
    </ResourceRadioGroup>
  );
};

export const OptimizationThresholdField = ({
  LabelComponent,
  size = 'medium',
  layout = 'vertical',
}: {
  LabelComponent?: FunctionComponent<PropsWithChildren>;
  size?: 'medium' | 'large';
  layout?: 'horizontal' | 'vertical';
}) => {
  return (
    <Stack
      rowGap={16}
      columnGap={24}
      data-testid="optimization-threshold-field"
      direction={layout === 'horizontal' ? 'row' : 'column'}
    >
      <ResourceThresholdInput
        resource="cpu"
        LabelComponent={LabelComponent}
        size={size}
      />
      <Divider orientation="vertical" flexItem />
      <ResourceThresholdInput
        resource="memory"
        LabelComponent={LabelComponent}
        size={size}
      />
    </Stack>
  );
};
